import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import AddressAutocomplete from "./AddressAutocomplete";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { WarningCircle } from "@phosphor-icons/react";

const EMPTY = { line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", formatted: "", verified: false };

export default function CaseAddressPrompt({ caseData, onUpdated }) {
    const [open, setOpen] = useState(false);
    const [debtorAddr, setDebtorAddr] = useState(EMPTY);
    const [creditorAddr, setCreditorAddr] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setDebtorAddr(caseData?.debtor_address ? { ...EMPTY, ...caseData.debtor_address } : EMPTY);
        setCreditorAddr(caseData?.creditor_address ? { ...EMPTY, ...caseData.creditor_address } : EMPTY);
    }, [caseData?.id, caseData?.debtor_address, caseData?.creditor_address]);

    if (!caseData) return null;
    const isValid = (a) => a?.line1 && a?.city && a?.state && a?.postal_code;
    const missing = !isValid(caseData.debtor_address) || !isValid(caseData.creditor_address);

    const missingFieldsLabel = (a) => {
        const miss = [];
        if (!a?.line1) miss.push("street");
        if (!a?.city) miss.push("city");
        if (!a?.state) miss.push("state");
        if (!a?.postal_code) miss.push("ZIP");
        return miss.join(", ");
    };

    const save = async () => {
        if (!isValid(debtorAddr) || !isValid(creditorAddr)) {
            const parts = [];
            if (!isValid(debtorAddr)) parts.push(`Debtor missing: ${missingFieldsLabel(debtorAddr) || "all fields"}`);
            if (!isValid(creditorAddr)) parts.push(`Creditor missing: ${missingFieldsLabel(creditorAddr) || "all fields"}`);
            toast.error(parts.join(" · "));
            return;
        }
        setSaving(true);
        try {
            await api.patch(`/cases/${caseData.id}`, {
                debtor_address: debtorAddr,
                creditor_address: creditorAddr,
            });
            toast.success("Mailing addresses saved");
            setOpen(false);
            if (onUpdated) onUpdated();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            {missing ? (
                <div
                    data-testid="address-missing-banner"
                    className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-md"
                >
                    <WarningCircle size={20} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-medium text-amber-900">Mailing addresses missing</p>
                        <p className="text-xs text-amber-800 mt-0.5">
                            AI-generated dispute and settlement letters need both mailing addresses to produce proper letterhead and reference lines.
                        </p>
                    </div>
                    <Button
                        size="sm"
                        onClick={() => setOpen(true)}
                        data-testid="address-add-btn"
                        className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                    >
                        Add addresses
                    </Button>
                </div>
            ) : (
                <div className="flex items-center justify-end">
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        data-testid="address-edit-btn"
                        className="text-xs text-slate-500 underline hover:text-[#0F1E3D]"
                    >
                        Edit mailing addresses
                    </button>
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="address-dialog">
                    <DialogHeader>
                        <DialogTitle>Mailing addresses</DialogTitle>
                        <DialogDescription>
                            Both addresses will appear at the top of every AI-generated dispute and settlement letter.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid md:grid-cols-2 gap-6 py-2">
                        <AddressAutocomplete
                            label="Debtor / Client mailing address"
                            value={debtorAddr}
                            onChange={setDebtorAddr}
                            required
                            testIdPrefix="dlg-debtor"
                            onValidate={(payload) => api.post("/address/validate", payload).then((r) => r.data).catch(() => null)}
                        />
                        <AddressAutocomplete
                            label="Creditor mailing address"
                            value={creditorAddr}
                            onChange={setCreditorAddr}
                            required
                            testIdPrefix="dlg-creditor"
                            onValidate={(payload) => api.post("/address/validate", payload).then((r) => r.data).catch(() => null)}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={save} disabled={saving} data-testid="address-save-btn">
                            {saving ? "Saving…" : "Save addresses"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
